import { ResponsiveHeatMapCanvas } from '@nivo/heatmap';
import { json, type LoaderFunctionArgs } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { addDays } from 'date-fns';
import { format } from 'date-fns-tz';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getHeatmapModel } from '#app/models/heatmap/index.ts';
import {
  type HeatmapInputData,
  type HeatmapData,
  type HeatmapSchema,
} from '#app/models/heatmap/types.ts';
import {
  getNumberOfDays,
  mapBucketToDate,
  DEFAULT_PERIOD_IN_MINUTES,
} from '#app/models/heatmap/utils.ts';
import { WidgetCard } from '#app/routes/widgets+/__widget-card.tsx';
import { WidgetPageContainer } from '#app/routes/widgets+/__widget-page-container.tsx';
import { type WidgetProps } from '#app/routes/widgets+/__widget-types.ts';
import { getDefaultEndDate, getDefaultStartDate } from '#app/utils/dates.tsx';
import { prisma } from '#app/utils/db.server.ts';
import { getWidgetData } from '#app/utils/get-widget-data.ts';
import { getDomainUrl } from '#app/utils/misc.tsx';
import { sanitizeOptions } from './__widget-utils.ts';

export async function loader({ request }: LoaderFunctionArgs) {
  const { searchParams } = new URL(request.url);
  const origin = getDomainUrl(request);
  const options = sanitizeOptions(searchParams, {
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(searchParams),
  });
  const heatmapModel = getHeatmapModel(options.startDate, options.endDate);
  const heatmapConfig = await prisma.chart.findFirst({
    where: {
      type: 'heatmap',
      id: options.widgetId,
    },
  });

  if (
    heatmapConfig == null ||
    heatmapConfig.data == null ||
    heatmapConfig.schema == null
  ) {
    throw new Response('Not Found', { status: 404 });
  }

  const data = await getWidgetData<HeatmapInputData>(heatmapConfig, origin, {
    startDate: options.startDate,
    endDate: options.endDate,
  });
  const chart = heatmapModel.buildChart(heatmapConfig, data);

  return json(
    {
      options,
      chart,
    },
    {
      headers: {
        'Cache-Control': 'immutable, public, max-age=3600',
      },
    },
  );
}

export default function HeatmapPage() {
  const data = useLoaderData<typeof loader>();
  const { chart, options } = data;
  const {
    width = 800,
    type,
    theme,
    startDate,
    endDate,
    dashboardSlug,
  } = options;
  return (
    <WidgetPageContainer
      type={type}
      width={width}
      startDate={startDate}
      endDate={endDate}
      dashboardSlug={dashboardSlug}
    >
      <Heatmap
        id={chart.id}
        data={chart.data}
        theme={theme}
        startDate={startDate}
        endDate={endDate}
        schema={chart.schema as HeatmapSchema}
        readonly={true}
        showExport={false}
      />
    </WidgetPageContainer>
  );
}

function getCSVData(data: HeatmapData) {
  return data
    .flatMap((d) => {
      return d.data.map(({ x, y, ...originalData }) => ({
        ...originalData,
      }));
    })
    .filter((d) => Object.keys(d).length > 0);
}

export function Heatmap(
  props: WidgetProps<any> & {
    schema: HeatmapSchema;
    startDate: string;
    endDate: string;
  },
) {
  const {
    id,
    data,
    readonly,
    onSelectExport,
    theme,
    showExport,
    title,
    schema: { subtitle },
    startDate,
    endDate: _endDate,
  } = props;
  const endDate = format(addDays(new Date(_endDate), 1), 'yyyy-MM-dd', {
    timeZone: 'Europe/Madrid',
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [tickValues, setTickValues] = useState<{ x: string[]; y: string[] }>({
    x: [],
    y: [],
  });
  const colors = [
    '#182E6B',
    '#36378E',
    '#713984',
    '#97386E',
    '#B7375A',
    '#EF4C55',
  ];
  const labels = ['A', 'B', 'C', 'D', 'E', 'F'];

  const emptyColor = theme === 'light' ? '#ffffff' : '#1A1A25';
  const borderColor = '#323246';
  const textColor = theme === 'light' ? '#1a1a25' : '#ececec';
  const fontSize = 12;

  const height = 100;
  const margin = { top: 20, right: 48, bottom: 40, left: 62 };

  const yLength = data.length;
  let numberOfDays = getNumberOfDays(startDate, endDate);

  useEffect(() => {
    function handleResize() {
      const intervalTicks = new Array(numberOfDays * 24)
        .fill(0)
        .map((_, i) => i * (60 / DEFAULT_PERIOD_IN_MINUTES));
      const yValues = new Array(yLength).fill(0).map((_, i) => i);
      const labelWidth = numberOfDays > 1 ? fontSize * 12 : fontSize * 5;
      const labelHeight = fontSize * 3;
      let valuesX, valuesY;
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        const containerWidth = width - margin.left - margin.right;
        const containerHeight = height - margin.bottom;

        const ticksX = Math.ceil(containerWidth / labelWidth);
        const stepX = Math.max(Math.ceil(intervalTicks.length / ticksX), 1);
        valuesX = intervalTicks;
        if (intervalTicks.length > ticksX) {
          valuesX = intervalTicks.filter((_, i) => i === 0 || i % stepX === 0);
        }

        const ticksY = Math.floor(containerHeight / labelHeight) - 1;
        const stepY = Math.max(Math.floor(yLength / ticksY), 1);
        valuesY = yValues;
        if (yValues.length > ticksY) {
          valuesY = yValues.filter((i) => i === 0 || i % stepY === 0);
        }
      } else {
        valuesX = intervalTicks.filter((_, i) => i % 2 === 0);
        valuesY = yValues.filter((i) => i % 2 === 0);
      }
      setTickValues({
        x: valuesX.map((v) => v.toString()),
        y: valuesY.map((v) => v.toString()),
      });
    }
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [margin.left, margin.right, margin.bottom, yLength, numberOfDays]);

  const csvData = useMemo(() => getCSVData(data), [data]);

  return (
    <WidgetCard
      id={id}
      data={csvData}
      title={title as string}
      titleClassName="max-w-[400px]"
      readonly={readonly}
      widgetType="heatmap"
      showExport={showExport}
      onSelectExport={onSelectExport}
      legend={<HeatmapLegend colors={colors} labels={labels} />}
    >
      <div>
        <p className="mb-3 ml-[26px] text-body text-primary">{subtitle}</p>
        <div style={{ height }} ref={containerRef}>
          <ResponsiveHeatMapCanvas
            data={data}
            theme={{
              text: {
                fill: textColor,
                fontSize,
                fontFamily: 'roboto',
              },
              axis: {
                ticks: {
                  text: {
                    fill: textColor,
                    fontSize,
                    fontFamily: 'Roboto Mono, monospace',
                  },
                },
              },
              grid: {
                line: {
                  stroke: borderColor,
                },
              },
            }}
            margin={margin}
            valueFormat=">-.2s"
            axisBottom={{
              tickSize: 0,
              tickPadding: 12,
              tickValues: tickValues.x,
              format: (d: string) => {
                const date = mapBucketToDate(Number(d), startDate);
                const time = format(date, 'HH:mm', {
                  timeZone: 'Europe/Madrid',
                });
                if (numberOfDays > 1) {
                  const dayMonth = format(date, 'dd/MM', {
                    timeZone: 'Europe/Madrid',
                  });

                  return `${time}h ${dayMonth}`;
                }
                return `${time}h`;
              },
            }}
            axisRight={null}
            axisLeft={{
              tickSize: 0,
              tickPadding: 12,
              tickValues: tickValues.y.map((i) => data[Number(i)]?.id),
            }}
            axisTop={null}
            colors={(cell) =>
              cell.value != null ? colors[cell.value] : '#555555'
            }
            emptyColor={emptyColor}
            borderWidth={1}
            enableLabels={false}
            isInteractive={!readonly}
            hoverTarget="cell"
            inactiveOpacity={0.2}
            tooltip={(data: any) => {
              const {
                cell: {
                  data: { x, y, ...d },
                  serieId,
                },
              } = data;
              const date = mapBucketToDate(x, startDate);
              const time = format(date, 'HH:mm', {
                timeZone: 'Europe/Madrid',
              });
              const dayMonth = format(date, 'dd/MM', {
                timeZone: 'Europe/Madrid',
              });

              return (
                <div className="w-[250px] rounded-sm bg-accent px-3 py-6">
                  <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                    {y != null && (
                      <div className="flex flex-col">
                        <p className="text-label text-accent-foreground">
                          Nivel de servicio:
                        </p>
                        <p className="inline-flex">
                          <span
                            className="mr-2 block h-5 w-2"
                            style={{ backgroundColor: colors[y] }}
                          />
                          <span className="text-body text-primary">
                            {labels[y]}
                          </span>
                        </p>
                      </div>
                    )}
                    {d.densidad != null && (
                      <div className="flex flex-col">
                        <p className="text-label text-accent-foreground">
                          Densidad:
                        </p>
                        <p>
                          <span className="text-body text-primary">
                            {Number(d.densidad).toFixed(2)}
                          </span>
                        </p>
                      </div>
                    )}
                    <div className="flex flex-col">
                      <p className="text-label text-accent-foreground">Pk:</p>
                      <p>
                        <span className="text-body text-primary">
                          {serieId.replace('Pk ', '')}
                        </span>
                      </p>
                    </div>
                    <div className="flex flex-col">
                      <p className="text-label text-accent-foreground">Hora:</p>
                      <p>
                        <span className="text-body text-primary">
                          {time}h {dayMonth}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
    </WidgetCard>
  );
}

function HeatmapLegend({
  colors,
  labels,
}: {
  colors: string[];
  labels: string[];
}) {
  return (
    <div className="flex items-center">
      {colors.map((color, index) => (
        <div key={color} className="flex flex-col items-center">
          <div className="h-6 w-6" style={{ backgroundColor: color }} />
          <span className="text-body">{labels[index]}</span>
        </div>
      ))}
    </div>
  );
}
